/**
 * Tenant subscription contracts — mirrors `platform.tenant_subscriptions`.
 *
 * One subscription per tenant, with billing cycle, current period and
 * optional trial/cancellation timestamps.
 */
import { z } from 'zod';

import { SubscriptionPlanIdSchema } from './subscription-plan.js';

// ---------------------------------------------------------------------------
// Enum schemas
// ---------------------------------------------------------------------------

export const SubscriptionStatusSchema = z.enum([
  'trial',
  'active',
  'past_due',
  'cancelled',
  'suspended',
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const BillingCycleSchema = z.enum(['monthly', 'yearly']);
export type BillingCycle = z.infer<typeof BillingCycleSchema>;

// ---------------------------------------------------------------------------
// Record (output)
// ---------------------------------------------------------------------------

export const TenantSubscriptionSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  planId: SubscriptionPlanIdSchema,
  status: SubscriptionStatusSchema,
  billingCycle: BillingCycleSchema,
  currentPeriodStart: z.string(), // YYYY-MM-DD date string
  currentPeriodEnd: z.string(), // YYYY-MM-DD date string
  trialEndsAt: z.string().nullable(), // YYYY-MM-DD date string
  cancelledAt: z.string().datetime({ offset: true }).nullable(),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});
export type TenantSubscription = z.infer<typeof TenantSubscriptionSchema>;

// ---------------------------------------------------------------------------
// Update input
// ---------------------------------------------------------------------------

export const UpdateTenantSubscriptionInputSchema = z.object({
  planId: z.string().uuid().optional(),
  status: SubscriptionStatusSchema.optional(),
  billingCycle: BillingCycleSchema.optional(),
  currentPeriodStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  currentPeriodEnd: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  trialEndsAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  cancelledAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export type UpdateTenantSubscriptionInput = z.infer<typeof UpdateTenantSubscriptionInputSchema>;
